/**
 * Default translations for the Pritect Beacon consent banner.
 *
 * Bundled with the consent script so customers get correct translations
 * out of the box. Customers can override any string via the `custom_copy`
 * JSONB field in their banner configuration.
 *
 * Keys:
 *   title           : Banner heading
 *   body            : GDPR consent body text. {purposes} is replaced with the
 *                     enabled categories' purpose_* phrases as a list
 *   bodyNoOptional  : Body text when the banner offers no optional category
 *   withdrawNotice  : How to change or withdraw consent, shown on the first layer
 *   bodyOptOut      : CCPA opt-out body text
 *   acceptAll       : Accept all button
 *   rejectAll       : GDPR reject all button
 *   rejectAllOptOut : CCPA opt-out button
 *   manage          : Manage preferences button
 *   savePrefs       : Save preferences button
 *   close           : Close button aria-label
 *   alwaysActive    : Strictly necessary toggle aria-label and state text
 *   showVendors     : Show vendor list button
 *   hideVendors     : Hide vendor list button
 *   showCookies     : Preferences panel button that lists a category's cookies
 *                     (use {count} placeholder)
 *   hideCookies     : Button that hides that list again
 *   vendorCount     : Vendor list heading (use {count} placeholder)
 *   policy          : Vendor policy link text
 *   privacyPolicy   : First-layer link to the controller's privacy policy
 *   cookiePolicy    : First-layer link to the controller's cookie policy
 *   poweredBy       : Powered by text
 *   privacySettings : Preference center button label
 *   stateAccepted   : "Your current state" text for a consented category
 *   stateRefused    : "Your current state" text for a refused category
 *   cat_*           : Category labels
 *   catDesc_*       : Category descriptions
 *   purpose_*       : Short purpose phrases that fill {purposes} in body
 *   lawful_*        : Lawful basis labels in the cookie declaration
 */

export interface TranslationStrings {
  title: string;
  body: string;
  bodyOptOut: string;
  acceptAll: string;
  rejectAll: string;
  rejectAllOptOut: string;
  manage: string;
  savePrefs: string;
  close: string;
  alwaysActive: string;
  showVendors: string;
  hideVendors: string;
  showCookies: string;
  hideCookies: string;
  vendorCount: string;
  policy: string;
  poweredBy: string;
  privacySettings: string;
  cat_strictly_necessary: string;
  cat_analytics: string;
  cat_advertising: string;
  cat_functional: string;
  cat_social: string;
  catDesc_strictly_necessary: string;
  catDesc_analytics: string;
  catDesc_advertising: string;
  catDesc_functional: string;
  catDesc_social: string;
  yourCurrentState: string;
  showDetails: string;
  hideDetails: string;
  consentDate: string;
  consentId: string;
  cat_unclassified: string;
  catDesc_unclassified: string;
  declTitle: string;
  declCookieCount: string;
  declCookieCountOne: string;
  declLastScanned: string;
  declNever: string;
  declColCookie: string;
  declColProvider: string;
  declColPurpose: string;
  declColDuration: string;
  declColType: string;
  declColLawfulBasis: string;
  declFirstParty: string;
  declThirdParty: string;
  declSession: string;
  declEmpty: string;
  declError: string;
  declUpdated: string;
  withdrawNotice: string;
  bodyNoOptional: string;
  purpose_analytics: string;
  purpose_advertising: string;
  purpose_functional: string;
  purpose_social: string;
  privacyPolicy: string;
  cookiePolicy: string;
  stateAccepted: string;
  stateRefused: string;
  logoAlt: string;
  otherPurposes: string;
  lawful_consent: string;
  lawful_legitimate_interest: string;
  lawful_contract: string;
  lawful_legal_obligation: string;
  lawful_vital_interest: string;
  lawful_public_task: string;
}

export const defaultTranslations: Record<string, TranslationStrings> = {
  en: {
    title: "Your privacy choices",
    body: "This site uses cookies that it needs to work. With your consent, it also uses optional cookies for {purposes}. Choose which optional cookies you accept.",
    bodyOptOut: "We use cookies and similar technologies to enhance your experience. You can opt out of non-essential data collection below.",
    acceptAll: "Accept all",
    rejectAll: "Reject all",
    rejectAllOptOut: "Opt out",
    manage: "Manage preferences",
    savePrefs: "Save preferences",
    close: "Close",
    alwaysActive: "Always active",
    showVendors: "Show vendor list",
    hideVendors: "Hide vendor list",
    showCookies: "Show cookies ({count})",
    hideCookies: "Hide cookies",
    vendorCount: "Vendor list ({count} vendors)",
    policy: "Policy",
    poweredBy: "Powered by",
    privacySettings: "Privacy settings",
    cat_strictly_necessary: "Strictly necessary",
    cat_analytics: "Analytics",
    cat_advertising: "Advertising",
    cat_functional: "Functional",
    cat_social: "Social media",
    catDesc_strictly_necessary: "Required for the website to function. Cannot be disabled.",
    catDesc_analytics: "Help us understand how visitors interact with our website.",
    catDesc_advertising: "Used to show relevant ads and track ad campaign effectiveness.",
    catDesc_functional: "Enable enhanced features like live chat and saved preferences.",
    catDesc_social: "Allow social media sharing and embedding.",
    yourCurrentState: "Your current state",
    showDetails: "Show details",
    hideDetails: "Hide details",
    consentDate: "Consent date",
    consentId: "Consent ID",
    cat_unclassified: "Unclassified",
    catDesc_unclassified: "Cookies that have not yet been categorized.",
    declTitle: "Cookie declaration",
    declCookieCount: "{count} cookies in use on {domain}",
    declCookieCountOne: "1 cookie in use on {domain}",
    declLastScanned: "Last scanned",
    declNever: "Never",
    declColCookie: "Cookie",
    declColProvider: "Provider",
    declColPurpose: "Purpose",
    declColDuration: "Duration",
    declColType: "Type",
    declColLawfulBasis: "Lawful basis",
    declFirstParty: "1st party",
    declThirdParty: "3rd party",
    declSession: "Session",
    declEmpty: "No cookies have been discovered yet. Run a scan to populate this declaration.",
    declError: "Cookie declaration unavailable. Please try again later.",
    declUpdated: "Updated",
    withdrawNotice: "You can change or withdraw your consent at any time in Privacy settings.",
    bodyNoOptional: "This site only uses cookies that it needs to work.",
    purpose_analytics: "measuring how the site is used",
    purpose_advertising: "showing relevant ads and measuring them",
    purpose_functional: "extra features such as chat",
    purpose_social: "social media sharing and embedded content",
    privacyPolicy: "Privacy policy",
    cookiePolicy: "Cookie policy",
    stateAccepted: "Accepted",
    stateRefused: "Refused",
    logoAlt: "Logo",
    otherPurposes: "Other",
    lawful_consent: "Consent",
    lawful_legitimate_interest: "Legitimate interest",
    lawful_contract: "Contract",
    lawful_legal_obligation: "Legal obligation",
    lawful_vital_interest: "Vital interest",
    lawful_public_task: "Public task",
  },

  fr: {
    title: "Vos choix en matière de confidentialité",
    body: "Ce site utilise des cookies nécessaires à son fonctionnement. Avec votre consentement, il utilise aussi des cookies facultatifs pour {purposes}. Choisissez les cookies facultatifs que vous acceptez.",
    bodyOptOut: "Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience. Vous pouvez refuser la collecte de données non essentielles ci-dessous.",
    acceptAll: "Tout accepter",
    rejectAll: "Tout refuser",
    rejectAllOptOut: "Refuser",
    manage: "Gérer les préférences",
    savePrefs: "Enregistrer les préférences",
    close: "Fermer",
    alwaysActive: "Toujours actif",
    showVendors: "Afficher la liste des fournisseurs",
    hideVendors: "Masquer la liste des fournisseurs",
    showCookies: "Afficher les cookies ({count})",
    hideCookies: "Masquer les cookies",
    vendorCount: "Liste des fournisseurs ({count} fournisseurs)",
    policy: "Politique",
    poweredBy: "Propulsé par",
    privacySettings: "Paramètres de confidentialité",
    cat_strictly_necessary: "Strictement nécessaires",
    cat_analytics: "Analytiques",
    cat_advertising: "Publicitaires",
    cat_functional: "Fonctionnels",
    cat_social: "Réseaux sociaux",
    catDesc_strictly_necessary: "Nécessaires au fonctionnement du site. Ne peuvent pas être désactivés.",
    catDesc_analytics: "Nous aident à comprendre comment les visiteurs interagissent avec notre site.",
    catDesc_advertising: "Utilisés pour afficher des publicités pertinentes et mesurer l'efficacité des campagnes.",
    catDesc_functional: "Permettent des fonctionnalités avancées comme le chat en direct et les préférences enregistrées.",
    catDesc_social: "Permettent le partage et l'intégration de contenu sur les réseaux sociaux.",
    yourCurrentState: "Votre état actuel",
    showDetails: "Afficher les détails",
    hideDetails: "Masquer les détails",
    consentDate: "Date du consentement",
    consentId: "ID de consentement",
    cat_unclassified: "Non classés",
    catDesc_unclassified: "Cookies qui n'ont pas encore été catégorisés.",
    declTitle: "Déclaration des cookies",
    declCookieCount: "{count} cookies utilisés sur {domain}",
    declCookieCountOne: "1 cookie utilisé sur {domain}",
    declLastScanned: "Dernière analyse",
    declNever: "Jamais",
    declColCookie: "Cookie",
    declColProvider: "Fournisseur",
    declColPurpose: "Finalité",
    declColDuration: "Durée",
    declColType: "Type",
    declColLawfulBasis: "Base juridique",
    declFirstParty: "1re partie",
    declThirdParty: "Tierce partie",
    declSession: "Session",
    declEmpty: "Aucun cookie n'a encore été découvert. Lancez une analyse pour remplir cette déclaration.",
    declError: "Déclaration des cookies indisponible. Veuillez réessayer plus tard.",
    declUpdated: "Mis à jour le",
    withdrawNotice: "Vous pouvez modifier ou retirer votre consentement à tout moment dans les paramètres de confidentialité.",
    bodyNoOptional: "Ce site utilise uniquement des cookies nécessaires à son fonctionnement.",
    purpose_analytics: "mesurer l'utilisation du site",
    purpose_advertising: "afficher des publicités pertinentes et en mesurer l'efficacité",
    purpose_functional: "des fonctionnalités supplémentaires comme le chat",
    purpose_social: "le partage sur les réseaux sociaux et les contenus intégrés",
    privacyPolicy: "Politique de confidentialité",
    cookiePolicy: "Politique relative aux cookies",
    stateAccepted: "Accepté",
    stateRefused: "Refusé",
    logoAlt: "Logo",
    otherPurposes: "Autres",
    lawful_consent: "Consentement",
    lawful_legitimate_interest: "Intérêt légitime",
    lawful_contract: "Contrat",
    lawful_legal_obligation: "Obligation légale",
    lawful_vital_interest: "Intérêts vitaux",
    lawful_public_task: "Mission d'intérêt public",
  },

  de: {
    title: "Ihre Datenschutzeinstellungen",
    body: "Diese Website verwendet Cookies, die für ihren Betrieb erforderlich sind. Mit Ihrer Einwilligung verwendet sie außerdem optionale Cookies für {purposes}. Bitte wählen Sie, welche optionalen Cookies Sie akzeptieren.",
    bodyOptOut: "Wir verwenden Cookies und ähnliche Technologien, um Ihre Erfahrung zu verbessern. Sie können die nicht wesentliche Datenerfassung unten ablehnen.",
    acceptAll: "Alle akzeptieren",
    rejectAll: "Alle ablehnen",
    rejectAllOptOut: "Ablehnen",
    manage: "Einstellungen verwalten",
    savePrefs: "Einstellungen speichern",
    close: "Schließen",
    alwaysActive: "Immer aktiv",
    showVendors: "Anbieterliste anzeigen",
    hideVendors: "Anbieterliste ausblenden",
    showCookies: "Cookies anzeigen ({count})",
    hideCookies: "Cookies ausblenden",
    vendorCount: "Anbieterliste ({count} Anbieter)",
    policy: "Richtlinie",
    poweredBy: "Betrieben von",
    privacySettings: "Datenschutzeinstellungen",
    cat_strictly_necessary: "Unbedingt erforderlich",
    cat_analytics: "Analyse",
    cat_advertising: "Werbung",
    cat_functional: "Funktional",
    cat_social: "Soziale Medien",
    catDesc_strictly_necessary: "Für die Funktion der Website erforderlich. Können nicht deaktiviert werden.",
    catDesc_analytics: "Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.",
    catDesc_advertising: "Werden verwendet, um relevante Werbung anzuzeigen und die Wirksamkeit von Kampagnen zu messen.",
    catDesc_functional: "Ermöglichen erweiterte Funktionen wie Live-Chat und gespeicherte Einstellungen.",
    catDesc_social: "Ermöglichen das Teilen und Einbetten von Inhalten in sozialen Medien.",
    yourCurrentState: "Ihr aktueller Status",
    showDetails: "Details anzeigen",
    hideDetails: "Details ausblenden",
    consentDate: "Einwilligungsdatum",
    consentId: "Einwilligungs-ID",
    cat_unclassified: "Nicht klassifiziert",
    catDesc_unclassified: "Cookies, die noch nicht kategorisiert wurden.",
    declTitle: "Cookie-Erklärung",
    declCookieCount: "{count} Cookies auf {domain} im Einsatz",
    declCookieCountOne: "1 Cookie auf {domain} im Einsatz",
    declLastScanned: "Zuletzt gescannt",
    declNever: "Nie",
    declColCookie: "Cookie",
    declColProvider: "Anbieter",
    declColPurpose: "Zweck",
    declColDuration: "Dauer",
    declColType: "Typ",
    declColLawfulBasis: "Rechtsgrundlage",
    declFirstParty: "Erstanbieter",
    declThirdParty: "Drittanbieter",
    declSession: "Sitzung",
    declEmpty: "Es wurden noch keine Cookies entdeckt. Führen Sie einen Scan durch, um diese Erklärung zu füllen.",
    declError: "Cookie-Erklärung nicht verfügbar. Bitte versuchen Sie es später erneut.",
    declUpdated: "Aktualisiert",
    withdrawNotice: "Sie können Ihre Einwilligung jederzeit in den Datenschutzeinstellungen ändern oder widerrufen.",
    bodyNoOptional: "Diese Website verwendet nur Cookies, die für ihren Betrieb erforderlich sind.",
    purpose_analytics: "die Messung der Websitenutzung",
    purpose_advertising: "die Anzeige relevanter Werbung und deren Erfolgsmessung",
    purpose_functional: "Zusatzfunktionen wie den Chat",
    purpose_social: "das Teilen in sozialen Medien und eingebettete Inhalte",
    privacyPolicy: "Datenschutzerklärung",
    cookiePolicy: "Cookie-Richtlinie",
    stateAccepted: "Akzeptiert",
    stateRefused: "Abgelehnt",
    logoAlt: "Logo",
    otherPurposes: "Sonstige",
    lawful_consent: "Einwilligung",
    lawful_legitimate_interest: "Berechtigtes Interesse",
    lawful_contract: "Vertrag",
    lawful_legal_obligation: "Rechtliche Verpflichtung",
    lawful_vital_interest: "Lebenswichtige Interessen",
    lawful_public_task: "Öffentliche Aufgabe",
  },

  es: {
    title: "Sus opciones de privacidad",
    body: "Este sitio utiliza cookies necesarias para funcionar. Con su consentimiento, también utiliza cookies opcionales para {purposes}. Elija qué cookies opcionales acepta.",
    bodyOptOut: "Utilizamos cookies y tecnologías similares para mejorar su experiencia. Puede rechazar la recopilación de datos no esenciales a continuación.",
    acceptAll: "Aceptar todas",
    rejectAll: "Rechazar todas",
    rejectAllOptOut: "Rechazar",
    manage: "Gestionar preferencias",
    savePrefs: "Guardar preferencias",
    close: "Cerrar",
    alwaysActive: "Siempre activo",
    showVendors: "Mostrar lista de proveedores",
    hideVendors: "Ocultar lista de proveedores",
    showCookies: "Mostrar cookies ({count})",
    hideCookies: "Ocultar cookies",
    vendorCount: "Lista de proveedores ({count} proveedores)",
    policy: "Política",
    poweredBy: "Desarrollado por",
    privacySettings: "Configuración de privacidad",
    cat_strictly_necessary: "Estrictamente necesarias",
    cat_analytics: "Analíticas",
    cat_advertising: "Publicitarias",
    cat_functional: "Funcionales",
    cat_social: "Redes sociales",
    catDesc_strictly_necessary: "Necesarias para el funcionamiento del sitio web. No se pueden desactivar.",
    catDesc_analytics: "Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.",
    catDesc_advertising: "Se utilizan para mostrar anuncios relevantes y medir la efectividad de las campañas.",
    catDesc_functional: "Habilitan funciones avanzadas como el chat en vivo y las preferencias guardadas.",
    catDesc_social: "Permiten compartir e integrar contenido en redes sociales.",
    yourCurrentState: "Su estado actual",
    showDetails: "Mostrar detalles",
    hideDetails: "Ocultar detalles",
    consentDate: "Fecha de consentimiento",
    consentId: "ID de consentimiento",
    cat_unclassified: "Sin clasificar",
    catDesc_unclassified: "Cookies que aún no han sido categorizadas.",
    declTitle: "Declaración de cookies",
    declCookieCount: "{count} cookies en uso en {domain}",
    declCookieCountOne: "1 cookie en uso en {domain}",
    declLastScanned: "Último análisis",
    declNever: "Nunca",
    declColCookie: "Cookie",
    declColProvider: "Proveedor",
    declColPurpose: "Finalidad",
    declColDuration: "Duración",
    declColType: "Tipo",
    declColLawfulBasis: "Base jurídica",
    declFirstParty: "Primera parte",
    declThirdParty: "Terceros",
    declSession: "Sesión",
    declEmpty: "Aún no se han descubierto cookies. Ejecute un análisis para completar esta declaración.",
    declError: "Declaración de cookies no disponible. Inténtelo de nuevo más tarde.",
    declUpdated: "Actualizado",
    withdrawNotice: "Puede cambiar o retirar su consentimiento en cualquier momento en la configuración de privacidad.",
    bodyNoOptional: "Este sitio solo utiliza cookies necesarias para funcionar.",
    purpose_analytics: "medir el uso del sitio",
    purpose_advertising: "mostrar anuncios relevantes y medir su eficacia",
    purpose_functional: "funciones adicionales como el chat",
    purpose_social: "compartir en redes sociales y mostrar contenido integrado",
    privacyPolicy: "Política de privacidad",
    cookiePolicy: "Política de cookies",
    stateAccepted: "Aceptadas",
    stateRefused: "Rechazadas",
    logoAlt: "Logotipo",
    otherPurposes: "Otros",
    lawful_consent: "Consentimiento",
    lawful_legitimate_interest: "Interés legítimo",
    lawful_contract: "Contrato",
    lawful_legal_obligation: "Obligación legal",
    lawful_vital_interest: "Interés vital",
    lawful_public_task: "Misión de interés público",
  },

  it: {
    title: "Le tue scelte sulla privacy",
    body: "Questo sito utilizza i cookie necessari al suo funzionamento. Con il tuo consenso, utilizza anche cookie facoltativi per {purposes}. Scegli quali cookie facoltativi accetti.",
    bodyOptOut: "Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza. Puoi rifiutare la raccolta di dati non essenziali qui sotto.",
    acceptAll: "Accetta tutti",
    rejectAll: "Rifiuta tutti",
    rejectAllOptOut: "Rifiuta",
    manage: "Gestisci preferenze",
    savePrefs: "Salva preferenze",
    close: "Chiudi",
    alwaysActive: "Sempre attivo",
    showVendors: "Mostra elenco fornitori",
    hideVendors: "Nascondi elenco fornitori",
    showCookies: "Mostra i cookie ({count})",
    hideCookies: "Nascondi i cookie",
    vendorCount: "Elenco fornitori ({count} fornitori)",
    policy: "Informativa",
    poweredBy: "Offerto da",
    privacySettings: "Impostazioni sulla privacy",
    cat_strictly_necessary: "Strettamente necessari",
    cat_analytics: "Analitici",
    cat_advertising: "Pubblicitari",
    cat_functional: "Funzionali",
    cat_social: "Social media",
    catDesc_strictly_necessary: "Necessari per il funzionamento del sito. Non possono essere disattivati.",
    catDesc_analytics: "Ci aiutano a capire come i visitatori interagiscono con il nostro sito.",
    catDesc_advertising: "Utilizzati per mostrare annunci pertinenti e misurare l'efficacia delle campagne.",
    catDesc_functional: "Abilitano funzionalità avanzate come la chat dal vivo e le preferenze salvate.",
    catDesc_social: "Consentono la condivisione e l'integrazione di contenuti sui social media.",
    yourCurrentState: "Il tuo stato attuale",
    showDetails: "Mostra dettagli",
    hideDetails: "Nascondi dettagli",
    consentDate: "Data del consenso",
    consentId: "ID del consenso",
    cat_unclassified: "Non classificati",
    catDesc_unclassified: "Cookie non ancora categorizzati.",
    declTitle: "Dichiarazione sui cookie",
    declCookieCount: "{count} cookie in uso su {domain}",
    declCookieCountOne: "1 cookie in uso su {domain}",
    declLastScanned: "Ultima scansione",
    declNever: "Mai",
    declColCookie: "Cookie",
    declColProvider: "Fornitore",
    declColPurpose: "Finalità",
    declColDuration: "Durata",
    declColType: "Tipo",
    declColLawfulBasis: "Base giuridica",
    declFirstParty: "Prima parte",
    declThirdParty: "Terze parti",
    declSession: "Sessione",
    declEmpty: "Nessun cookie è stato ancora rilevato. Esegui una scansione per compilare questa dichiarazione.",
    declError: "Dichiarazione sui cookie non disponibile. Riprova più tardi.",
    declUpdated: "Aggiornato",
    withdrawNotice: "Puoi modificare o revocare il tuo consenso in qualsiasi momento nelle impostazioni sulla privacy.",
    bodyNoOptional: "Questo sito utilizza solo i cookie necessari al suo funzionamento.",
    purpose_analytics: "misurare l'utilizzo del sito",
    purpose_advertising: "mostrare annunci pertinenti e misurarne l'efficacia",
    purpose_functional: "funzioni aggiuntive come la chat",
    purpose_social: "la condivisione sui social media e i contenuti incorporati",
    privacyPolicy: "Informativa sulla privacy",
    cookiePolicy: "Cookie policy",
    stateAccepted: "Accettati",
    stateRefused: "Rifiutati",
    logoAlt: "Logo",
    otherPurposes: "Altro",
    lawful_consent: "Consenso",
    lawful_legitimate_interest: "Legittimo interesse",
    lawful_contract: "Contratto",
    lawful_legal_obligation: "Obbligo legale",
    lawful_vital_interest: "Interessi vitali",
    lawful_public_task: "Compito di interesse pubblico",
  },

  ar: {
    title: "خيارات الخصوصية الخاصة بك",
    body: "يستخدم هذا الموقع ملفات تعريف الارتباط اللازمة لعمله. وبموافقتك، يستخدم أيضًا ملفات تعريف ارتباط اختيارية من أجل {purposes}. يرجى اختيار ملفات تعريف الارتباط الاختيارية التي تقبلها.",
    bodyOptOut: "نستخدم ملفات تعريف الارتباط وتقنيات مماثلة لتحسين تجربتك. يمكنك رفض جمع البيانات غير الأساسية أدناه.",
    acceptAll: "قبول الكل",
    rejectAll: "رفض الكل",
    rejectAllOptOut: "رفض",
    manage: "إدارة التفضيلات",
    savePrefs: "حفظ التفضيلات",
    close: "إغلاق",
    alwaysActive: "نشط دائمًا",
    showVendors: "عرض قائمة المزودين",
    hideVendors: "إخفاء قائمة المزودين",
    showCookies: "عرض ملفات تعريف الارتباط ({count})",
    hideCookies: "إخفاء ملفات تعريف الارتباط",
    vendorCount: "قائمة المزودين ({count} مزود)",
    policy: "السياسة",
    poweredBy: "مدعوم من",
    privacySettings: "إعدادات الخصوصية",
    cat_strictly_necessary: "ضرورية للغاية",
    cat_analytics: "تحليلية",
    cat_advertising: "إعلانية",
    cat_functional: "وظيفية",
    cat_social: "وسائل التواصل الاجتماعي",
    catDesc_strictly_necessary: "مطلوبة لعمل الموقع. لا يمكن تعطيلها.",
    catDesc_analytics: "تساعدنا على فهم كيفية تفاعل الزوار مع موقعنا.",
    catDesc_advertising: "تُستخدم لعرض إعلانات ذات صلة وقياس فعالية الحملات الإعلانية.",
    catDesc_functional: "تُمكّن الميزات المتقدمة مثل الدردشة المباشرة والتفضيلات المحفوظة.",
    catDesc_social: "تسمح بمشاركة المحتوى وتضمينه على وسائل التواصل الاجتماعي.",
    yourCurrentState: "حالتك الحالية",
    showDetails: "عرض التفاصيل",
    hideDetails: "إخفاء التفاصيل",
    consentDate: "تاريخ الموافقة",
    consentId: "معرّف الموافقة",
    cat_unclassified: "غير مصنفة",
    catDesc_unclassified: "ملفات تعريف ارتباط لم يتم تصنيفها بعد.",
    declTitle: "بيان ملفات تعريف الارتباط",
    declCookieCount: "{count} ملفات تعريف ارتباط مستخدمة على {domain}",
    declCookieCountOne: "ملف تعريف ارتباط واحد مستخدم على {domain}",
    declLastScanned: "آخر فحص",
    declNever: "أبدًا",
    declColCookie: "ملف تعريف الارتباط",
    declColProvider: "المزود",
    declColPurpose: "الغرض",
    declColDuration: "المدة",
    declColType: "النوع",
    declColLawfulBasis: "الأساس القانوني",
    declFirstParty: "طرف أول",
    declThirdParty: "طرف ثالث",
    declSession: "جلسة",
    declEmpty: "لم يتم اكتشاف أي ملفات تعريف ارتباط بعد. قم بإجراء فحص لملء هذا البيان.",
    declError: "بيان ملفات تعريف الارتباط غير متاح. يرجى المحاولة مرة أخرى لاحقًا.",
    declUpdated: "تم التحديث",
    withdrawNotice: "يمكنك تغيير موافقتك أو سحبها في أي وقت من إعدادات الخصوصية.",
    bodyNoOptional: "يستخدم هذا الموقع فقط ملفات تعريف الارتباط اللازمة لعمله.",
    purpose_analytics: "قياس استخدام الموقع",
    purpose_advertising: "عرض إعلانات ملائمة وقياس فعاليتها",
    purpose_functional: "ميزات إضافية مثل الدردشة",
    purpose_social: "المشاركة عبر وسائل التواصل الاجتماعي والمحتوى المضمّن",
    privacyPolicy: "سياسة الخصوصية",
    cookiePolicy: "سياسة ملفات تعريف الارتباط",
    stateAccepted: "مقبولة",
    stateRefused: "مرفوضة",
    logoAlt: "الشعار",
    otherPurposes: "أخرى",
    lawful_consent: "الموافقة",
    lawful_legitimate_interest: "المصلحة المشروعة",
    lawful_contract: "العقد",
    lawful_legal_obligation: "الالتزام القانوني",
    lawful_vital_interest: "المصالح الحيوية",
    lawful_public_task: "المهمة العامة",
  },
};

/** RTL languages that require direction: rtl on the banner container */
export const RTL_LANGUAGES = new Set(["ar", "he", "fa", "ur"]);

/**
 * Join short phrases as a natural-language list ("a, b and c") in the banner
 * language, falling back to a comma list where Intl.ListFormat is missing.
 */
export function formatList(items: string[], lang: string): string {
  try {
    const ListFormat = (Intl as unknown as { ListFormat?: new (l: string, o: object) => { format(i: string[]): string } }).ListFormat;
    if (ListFormat) return new ListFormat(lang, { style: "long", type: "conjunction" }).format(items);
  } catch {
    // Unsupported locale: fall through
  }
  return items.join(", ");
}

/**
 * Format a date for the banner and the declaration: day first, named month,
 * year, in the visitor's language. English maps to en-GB, since the en-US
 * default puts the month first.
 */
export function formatDisplayDate(value: number | string | Date, lang: string, withTime = false): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = lang === "en" ? "en-GB" : lang;
  const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
  if (withTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
  }
  try {
    return date.toLocaleString(locale, options);
  } catch {
    return date.toLocaleString("en-GB", options);
  }
}

const HOUR = 3_600;
const DAY = 86_400;
/** Average Gregorian month, so 396 days reads as 13 months and 730 as 24. */
const MONTH_DAYS = 365.25 / 12;

function formatUnit(value: number, unit: "hour" | "day" | "month" | "year", lang: string): string {
  try {
    return new Intl.NumberFormat(lang, { style: "unit", unit, unitDisplay: "long" }).format(value);
  } catch {
    return `${value} ${unit}${value === 1 ? "" : "s"}`;
  }
}

/**
 * A cookie's lifetime in the visitor's language. The units follow the
 * register's own rule (describeLifetime in
 * supabase/functions/_shared/cookieLifetime.ts): hours under a day, then days,
 * whole years as years, months up to two years, years beyond. `fallback` is
 * the register's stored text, used only when no lifetime in seconds was
 * recorded (a declaration added by hand); "Session" is translated.
 */
export function formatLifetime(seconds: number | null | undefined, fallback: string | null | undefined, lang: string): string {
  if (seconds != null && Number.isFinite(seconds) && seconds > 0) {
    if (seconds < DAY) return formatUnit(Math.max(1, Math.round(seconds / HOUR)), "hour", lang);
    const days = Math.round(seconds / DAY);
    if (days < 30) return formatUnit(days, "day", lang);
    const months = Math.max(1, Math.round(days / MONTH_DAYS));
    if (months % 12 === 0) return formatUnit(months / 12, "year", lang);
    if (months < 24) return formatUnit(months, "month", lang);
    return formatUnit(Math.round(days / 365.25), "year", lang);
  }
  if (fallback && fallback !== "Session") return fallback;
  return resolveTranslation("declSession", lang);
}

/**
 * Resolve a translation string with 3-layer fallback:
 * 1. Customer override (custom_copy[lang][key])
 * 2. Bundled default (defaultTranslations[lang][key])
 * 3. English fallback (defaultTranslations["en"][key])
 */
export function resolveTranslation(
  key: keyof TranslationStrings,
  lang: string,
  customCopy?: Record<string, Record<string, string>> | null,
): string {
  const custom = customCopy?.[lang]?.[key];
  if (custom) return custom;

  const bundled = defaultTranslations[lang]?.[key];
  if (bundled) return bundled;

  return defaultTranslations["en"][key];
}
